import { hasDevToolingFrame } from './isDevToolingNoise';
import { isUnactionableClientNoise } from './isUnactionableClientNoise';

type SentryExceptionValue = {
  type?: string;
  value?: string;
  stacktrace?: { frames?: Array<{ filename?: string }> };
};

/** Minimal event shape used by Sentry `beforeSend` and unit tests. */
export type SentryDropCheckEvent = {
  exception?: { values?: SentryExceptionValue[] };
};

function syntheticErrorFromException(value: SentryExceptionValue): Error {
  const error = new Error(value.value || '');
  if (value.type) {
    error.name = value.type;
  }
  return error;
}

function eventCameFromDevTooling(event: SentryDropCheckEvent): boolean {
  const values = event.exception?.values ?? [];
  return values.some((value) =>
    hasDevToolingFrame((value.stacktrace?.frames ?? []).map((frame) => frame.filename)),
  );
}

function eventLooksLikeNoise(event: SentryDropCheckEvent): boolean {
  if (eventCameFromDevTooling(event)) {
    return true;
  }
  return (event.exception?.values ?? []).some((value) =>
    isUnactionableClientNoise(syntheticErrorFromException(value)),
  );
}

/**
 * True when this event is expected cancellation, HMR, a stale chunk, or a
 * resource-load Event rather than an application fault.
 *
 * `originalException` is preferred. Sentry event values are a fallback for
 * unhandled rejections where the SDK does not pass a real Error instance.
 */
export function shouldDropSentryEvent(
  event: SentryDropCheckEvent,
  originalException: unknown,
): boolean {
  return isUnactionableClientNoise(originalException) || eventLooksLikeNoise(event);
}
